import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { QrScanner } from "@/components/quality/QrScanner";
import { ScanResult } from "@/components/quality/ScanResult";
import { flushQueue } from "@/components/quality/ShopperRating";
import { GradeBadge } from "@/pages/QualityPassport";
import { parseScan, cachePassport, cachedPassport, readCache, readQueue, deviceId, getHub, setHub, HUBS } from "@/lib/qualityCache";
import { ScanLine, History, Wheat, RotateCcw, Wifi, WifiOff, Trophy } from "lucide-react";

export default function QualityScan() {
  const { lang } = useLang();
  const tr = lang === "tr";
  const [params, setParams] = useSearchParams();
  const [res, setRes] = useState(null); // {b, via, cached}
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(readCache());
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(readQueue().length);
  const posterHub = params.get("hub"); const fromPoster = params.get("src") === "poster";
  useEffect(() => {
    if (!(posterHub && HUBS.some(([k]) => k === posterHub))) return;
    setHub(posterHub);
    if (fromPoster) axios.post(`${API}/quality/hubs/${posterHub}/poster-scan`, { device_id: deviceId() }).catch(() => {});
  }, [posterHub, fromPoster]);

  const open = useCallback(async (idOrCode, via) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quality/batches/${idOrCode}`, { timeout: 8000 });
      cachePassport(data, via); setRecent(readCache()); setRes({ b: data, via, cached: false }); setParams({ b: data.id }, { replace: true });
      axios.post(`${API}/quality/batches/${data.id}/scan`, { device_id: deviceId(), via, hub: getHub() }).catch(() => {});
    } catch (er) {
      const c = cachedPassport(idOrCode);
      if (c && !er.response) { setRes({ b: c, via, cached: true }); toast.message(tr ? "Çevrimdışı — önbellekten gösteriliyor" : "Offline — showing cached passport"); }
      else toast.error(er.response?.status === 404 ? (tr ? "Parti bulunamadı" : "Batch not found") : (tr ? "Bağlantı yok ve önbellekte yok" : "No connection and not in the offline cache"));
    } finally { setLoading(false); }
  }, [setParams, tr]);

  const onCode = (text, via) => {
    if (!text) return toast.error(tr ? "Bu görselde QR kodu okunamadı" : "No QR code found in that image");
    const id = parseScan(text);
    if (!id) return toast.error(tr ? "Bu bir QuantumSpectra kalite QR'ı değil" : "Not a QuantumSpectra quality QR");
    open(id, via);
  };

  useEffect(() => { const b = params.get("b"); if (b && !res) open(b, "page"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const on = async () => { setOnline(true); const n = await flushQueue(); setQueued(readQueue().length); if (n) toast.success(tr ? `${n} bekleyen puan gönderildi` : `${n} queued rating(s) synced`); };
    const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off); if (navigator.onLine) on();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#050A15] text-slate-200 px-4 pb-16 pt-6" data-testid="quality-scan-page">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <Link to="/commons" className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#00FF7F]"><Wheat className="h-4 w-4" /> İmece · {tr ? "tezgâh tarayıcı" : "stall scanner"}</Link>
          <div className="flex items-center gap-3"><Link to="/quality/leaderboard" data-testid="scan-leaderboard-link" className="text-[10px] uppercase tracking-widest text-[#F59E0B] inline-flex items-center gap-1"><Trophy className="h-3 w-3" /> {tr ? "sıralama" : "leaderboard"}</Link><span className={`text-[10px] inline-flex items-center gap-1 ${online ? "text-slate-500" : "text-[#F59E0B]"}`} data-testid="scan-online-state">{online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />} {online ? "online" : tr ? "çevrimdışı" : "offline"}{queued ? ` · ${queued} ${tr ? "bekleyen" : "queued"}` : ""}</span></div>
        </div>
        {!res ? (
          <>
            <div>
              <h1 className="text-2xl text-slate-100 flex items-center gap-2" data-testid="scan-title"><ScanLine className="h-6 w-6 text-[#00FF7F]" /> {tr ? "QR'ı tara, kaliteyi gör" : "Scan the QR, see the quality"}</h1>
              <p className="text-xs text-slate-500 mt-1">{tr ? "Toprak → ürün → işlenmiş ürün notları, tek dokunuşla bütünlük doğrulaması ve alıcı puanı. Son taramalar çevrimdışı saklanır." : "Soil → produce → product grades, one-tap integrity check and shopper rating. Recent scans are kept offline."}</p>
            </div>
            <QrScanner onCode={onCode} tr={tr} />
            {loading && <div className="text-xs text-slate-400" data-testid="scan-loading">{tr ? "pasaport alınıyor…" : "fetching passport…"}</div>}
            {recent.length > 0 && (
              <div className="qs-panel p-3" data-testid="scan-recent">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1 mb-2"><History className="h-3 w-3" /> {tr ? "Son taramalar · çevrimdışı önbellek" : "Recent scans · offline cache"}</div>
                <ul className="space-y-1">{recent.slice(0, 8).map((c) => <li key={c.id}><button data-testid={`scan-recent-${c.code}`} onClick={() => (online ? open(c.id, "page") : setRes({ b: c, via: "page", cached: true }))} className="w-full flex items-center gap-2 text-left text-xs py-1 border-t border-white/5 hover:text-slate-100"><GradeBadge grade={c.overall.grade} size="sm" /><span className="flex-1 truncate text-slate-300">{c.product} <span className="font-mono text-slate-500">{c.code}</span></span><span className="text-[10px] text-slate-600 font-mono">{c.cached_at.slice(5, 16).replace("T", " ")}</span></button></li>)}</ul>
              </div>)}
          </>
        ) : (
          <>
            <ScanResult b={res.b} via={res.via} cached={res.cached} lang={lang} onRefresh={() => open(res.b.id, res.via)} />
            <button data-testid="scan-again" onClick={() => { setRes(null); setParams({}, { replace: true }); }} className="w-full border border-[#00FF7F]/60 text-[#00FF7F] py-3 text-sm inline-flex items-center justify-center gap-2 hover:bg-[#00FF7F]/10"><RotateCcw className="h-4 w-4" /> {tr ? "Başka bir QR tara" : "Scan another QR"}</button>
          </>)}
      </div>
    </div>
  );
}
